import { useState, FormEvent, useEffect } from 'react';
import { useAuth } from '../AuthProvider';
import { useNavigate } from 'react-router-dom';
import {
  View,
  Flex,
  TextInput,
  Button,
  Text,
  Link,
  Alert,
  IconEmailLine,
  IconLockLine,
  FormFieldGroup,
  FormField,
  Img,
  Heading,
} from '@smarterservices/smarter-ui';
import useTranslations from '../../../shared/hooks/useTranslations';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { login, verifyMfaCode, isLoading, isMfaRequired, isAuthenticated, user } = useAuth();
  const { t } = useTranslations();
  const navigate = useNavigate();

  // Handle redirection when authentication state changes
  useEffect(() => {
    if (isAuthenticated && user) {
      // Redirect based on user role
      switch (user.role) {
        case 'admin':
          navigate('/admin', { replace: true });
          break;
        case 'proctor':
          navigate('/proctor', { replace: true });
          break;
        case 'testTaker':
          navigate('/test-taker', { replace: true });
          break;
        case 'faculty':
          navigate('/faculty', { replace: true });
          break;
        default:
          // Fallback to public route if role is unknown
          navigate('/public', { replace: true });
      }
    }
  }, [isAuthenticated, user, navigate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    // If MFA is required, verify the MFA code
    if (isMfaRequired) {
      if (!mfaCode) {
        setError(t('login.error.missingMfaCode', 'Please enter the verification code'));
        return;
      }

      try {
        const success = await verifyMfaCode(mfaCode);
        if (!success) {
          setError(t('login.error.invalidMfaCode', 'Invalid verification code'));
        }
      } catch (err) {
        setError(t('login.error.general', 'An error occurred during verification'));
      }
      return;
    }

    // Initial login with email and password
    if (!email || !password) {
      setError(t('login.error.missingFields', 'Please enter both email and password'));
      return;
    }

    try {
      const success = await login(email, password);

      if (!success && !isMfaRequired) {
        setError(t('login.error.invalidCredentials', 'Invalid email or password'));
      }
    } catch (err) {
      setError(t('login.error.general', 'An error occurred during login'));
    }
  };

  // Render different content based on authentication state
  const renderContent = () => {
    // Render MFA verification form if MFA is required
    if (isMfaRequired) {
      return (
        <form onSubmit={handleSubmit}>
          <Flex direction="column" alignItems="center" margin="0 0 large 0">
            <Img
              src="https://cdn.prod.website-files.com/650b016f7e13a7d9de337f22/6629053ca1393100c09aa484_SmarterServices%20Logo-p-2600.png"
              alt={t('login.logo.alt', 'SmarterProctoring Logo')}
              width="90%"
            />
            <View as="div" margin="small 0 0 0">
              <Heading level="h2">
                {t('login.verificationRequired', 'Verification Required')}
              </Heading>
            </View>
            <View as="div" margin="small 0 0 0">
              <Text color="secondary">
                {t('login.enterVerificationCode', 'Please enter the 6-digit verification code')}
              </Text>
            </View>
          </Flex>

          {error && (
            <Alert variant="error" hasShadow={false} margin="0 0 medium 0">
              {error}
            </Alert>
          )}

          <FormFieldGroup description="" layout="stacked">
            <FormField id="mfaCode" label={t('login.mfaCode.label', 'Verification Code')}>
              <TextInput
                id="mfaCode"
                type="text"
                placeholder={t('login.mfaCode.placeholder', 'Enter 6-digit code')}
                value={mfaCode}
                onChange={e => setMfaCode(e.target.value)}
                renderBeforeInput={<IconLockLine />}
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
              />
            </FormField>
          </FormFieldGroup>

          <Button
            type="submit"
            color="primary"
            display="block"
            width="100%"
            margin="medium 0 0 0"
            disabled={isLoading}
          >
            {isLoading ? t('login.verifying', 'Verifying...') : t('login.verify', 'Verify')}
          </Button>
        </form>
      );
    }

    // Render initial login form
    return (
      <form onSubmit={handleSubmit}>
        <Flex direction="column" alignItems="center" margin="0 0 large 0">
          <Img
            src="https://cdn.prod.website-files.com/650b016f7e13a7d9de337f22/6629053ca1393100c09aa484_SmarterServices%20Logo-p-2600.png"
            alt={t('login.logo.alt', 'SmarterProctoring Logo')}
            width="90%"
          />
          <View as="div" margin="small 0 0 0">
            <Text color="secondary">{t('login.signIn', 'Sign in to your account')}</Text>
          </View>
        </Flex>

        {error && (
          <Alert variant="error" hasShadow={false}>
            {error}
          </Alert>
        )}

        <FormFieldGroup description="" layout="stacked">
          <FormField id="email" label={t('login.email.label', 'Email')}>
            <TextInput
              id="email"
              type="email"
              placeholder={t('login.email.placeholder', 'Enter your email')}
              value={email}
              onChange={e => setEmail(e.target.value)}
              renderBeforeInput={<IconEmailLine />}
            />
          </FormField>

          <FormField id="password" label={t('login.password.label', 'Password')}>
            <TextInput
              id="password"
              type="password"
              placeholder={t('login.password.placeholder', 'Enter your password')}
              value={password}
              onChange={e => setPassword(e.target.value)}
              renderBeforeInput={<IconLockLine />}
            />
          </FormField>
        </FormFieldGroup>

        <View as="div" textAlign="end" margin="0 0 medium 0">
          <Link href="/forgot-password" isWithinText={false}>
            {t('login.forgotPassword', 'Forgot password?')}
          </Link>
        </View>

        <Button type="submit" color="primary" display="block" width="100%" disabled={isLoading}>
          {isLoading ? t('login.signingIn', 'Signing in...') : t('login.signIn', 'Sign in')}
        </Button>
      </form>
    );
  };

  return (
    <View as="div" height="100vh" background="secondary">
      <Flex justifyItems="center" alignItems="center" height="100%">
        <Flex.Item>
          <View
            as="div"
            width="400px"
            background="primary"
            padding="large"
            borderRadius="medium"
            shadow="resting"
          >
            {renderContent()}
          </View>
        </Flex.Item>
      </Flex>
    </View>
  );
};

export default LoginPage;
